import { useState } from "react";
import { Link } from "wouter";
import { Instagram, Facebook, Mail, Phone, MapPin, Share2 } from "lucide-react";
import { BRAND } from "@/lib/data";
import Logo from "@/components/Logo";
import ShareSheet from "@/components/ShareSheet";

export default function Footer() {
  const [shareOpen, setShareOpen] = useState(false);

  return (
    <footer data-testid="site-footer" className="bg-spa-green text-spa-cream">
      <div className="container-spa py-20 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="mb-6">
              <Logo variant="light" size="md" />
            </div>
            <h3 className="font-serif text-3xl sm:text-4xl leading-[1.1] mb-6">
              Wellness, delivered to your door.
            </h3>
            <p className="font-sans text-spa-cream/70 max-w-md leading-relaxed">
              A premium mobile massage practice based in Knoxville. We bring the table, the oils,
              the warm towels — you bring the intention.
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="overline text-spa-gold mb-5">Explore</p>
            <ul className="space-y-3 font-sans text-spa-cream/80">
              <li><Link href="/services" data-testid="footer-link-services"><span className="hover:text-spa-cream cursor-pointer">Services</span></Link></li>
              <li><Link href="/about" data-testid="footer-link-about"><span className="hover:text-spa-cream cursor-pointer">About Stephanie</span></Link></li>
              <li><Link href="/products" data-testid="footer-link-products"><span className="hover:text-spa-cream cursor-pointer">Products</span></Link></li>
              <li><Link href="/gift-cards" data-testid="footer-link-gift-cards"><span className="hover:text-spa-cream cursor-pointer">Gift Cards</span></Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="overline text-spa-gold mb-5">Reach Us</p>
            <ul className="space-y-4 font-sans text-spa-cream/80">
              <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-1 text-spa-cyan shrink-0" strokeWidth={1.5} /><a data-testid="footer-phone" href={`tel:${BRAND.phoneRaw}`} className="hover:text-spa-cream">{BRAND.phone}</a></li>
              <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-1 text-spa-cyan shrink-0" strokeWidth={1.5} /><a data-testid="footer-email" href={`mailto:${BRAND.email}`} className="hover:text-spa-cream break-all">{BRAND.email}</a></li>
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-1 text-spa-cyan shrink-0" strokeWidth={1.5} /><span>{BRAND.serviceArea}</span></li>
              <li className="flex items-center gap-3 pt-2">
                <a href="https://www.instagram.com/masonmassage2025" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="w-9 h-9 rounded-full border border-spa-cream/30 flex items-center justify-center hover:bg-spa-cyan hover:border-spa-cyan transition-colors">
                  <Instagram className="w-4 h-4" strokeWidth={1.5} />
                </a>
                <a href="https://www.facebook.com/share/18k9PPvTGx/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="w-9 h-9 rounded-full border border-spa-cream/30 flex items-center justify-center hover:bg-spa-cyan hover:border-spa-cyan transition-colors">
                  <Facebook className="w-4 h-4" strokeWidth={1.5} />
                </a>
                <button onClick={() => setShareOpen(true)} aria-label="Share this site" data-testid="footer-share" className="w-9 h-9 rounded-full border border-spa-cream/30 flex items-center justify-center hover:bg-spa-cyan hover:border-spa-cyan transition-colors">
                  <Share2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-spa-cream/15 flex flex-col sm:flex-row justify-between gap-4 text-xs font-sans text-spa-cream/60">
          <p>&copy; {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-4">
            <p>Licensed · Insured · Knoxville, Tennessee</p>
            <span className="hidden sm:inline text-spa-cream/30">·</span>
            <Link href="/login" data-testid="footer-owner-login">
              <span className="cursor-pointer text-spa-cream/60 hover:text-spa-gold transition-colors">Owner Login</span>
            </Link>
          </div>
        </div>
      </div>

      <ShareSheet open={shareOpen} onOpenChange={setShareOpen} />
    </footer>
  );
}
